import { computed, type Ref } from 'vue'

import { Sport } from '@collector/sportsapi-client-legacy'
import { sportsApiClient } from '@desktop/globalState/sportsApiClient'
import { useEventId } from '@desktop/views/Relation/composables/useEventId'
import {
  queryOptions,
  useQuery,
  type UndefinedInitialQueryOptionsWithDataTag,
  type UseQueryReturnType,
} from '@tanstack/vue-query'

import {
  useCompetitionQuery,
  type CompetitionData,
} from './useCompetitionQuery'

type SportQueryKey = readonly ['event', 'sport', Ref<number>]

type SportQueryOptions = UndefinedInitialQueryOptionsWithDataTag<
  Sport,
  Error,
  Sport,
  SportQueryKey
>

export function sportQueryOptions(
  eventId: Ref<number>,
  competitionData: Ref<CompetitionData | undefined>,
): SportQueryOptions {
  return queryOptions({
    queryKey: ['event', 'sport', eventId] as const,
    enabled: computed(() => !!competitionData.value),
    queryFn: async (): Promise<Sport> => {
      const competition = competitionData.value?.competition
      if (!competition) {
        throw new Error('Competition query data missing for sport fetch')
      }
      const response = await sportsApiClient.Sport.get(
        competition.sport_id,
        competition.season.id,
      )

      return response.api.data.sport
    },
  })
}

export const useSportQuery = (
  options?: Omit<SportQueryOptions, 'queryKey' | 'queryFn'>,
): UseQueryReturnType<Sport, Error> => {
  const eventId = useEventId()
  const { data: competitionData } = useCompetitionQuery()

  return useQuery<Sport, Error, Sport, SportQueryKey>({
    ...sportQueryOptions(eventId, competitionData),
    ...options,
  })
}
